import React from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useGetCustomerById, useGetEmployeeById, useGetJobById, useGetTaskById } from '@shared/client-api';
import { usePathname, useRouter } from '@/i18n/routing';
import { BreadcrumbCurrentLink, BreadcrumbLink, BreadcrumbRoot } from '@/components/ui/breadcrumb';

interface BreadcrumbPath {
	slug: string;
	path: string;
	isLast: boolean;
}

interface BreadcrumbProps {
	onClose?: () => void;
}

export default function Breadcrumb({ onClose }: BreadcrumbProps) {
	const router = useRouter();
	const pathname = usePathname();
	const { ownerId, id } = useParams<{ ownerId?: string; id?: string }>();
	const g = useTranslations('General');
	const t = useTranslations('Nav');
	const jobId = useSearchParams().get('jobId') || undefined;

	const isEmployeeInPath = pathname.includes('employees');
	const isJobsInPath = pathname.includes('jobs');
	const isCustomerInPath = pathname.includes('customers');
	const isTaskInPath = pathname.includes('tasks');
	const isEditInPath = pathname.includes('edit');
	const isNewInPath = pathname.includes('new');

	// Fetch data only when needed
	const { data: taskData, isLoading: isTaskLoading } = useGetTaskById(id!, {
		query: {
			enabled: !!id && isTaskInPath,
		},
	});
	/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
	const { data: jobData, isLoading: isJobLoading } = useGetJobById(
		ownerId!,
		jobId ?? (isTaskInPath ? taskData?.jobId! : id!),
		{
			query: {
				enabled:
					!!ownerId &&
					((isTaskInPath && (!!jobId || (taskData !== undefined && taskData.jobId !== null))) ||
						(isJobsInPath && !!id)),
			},
		},
	);
	/* eslint-enable @typescript-eslint/no-non-null-asserted-optional-chain */
	const { data: employeeData, isLoading: isEmployeeLoading } = useGetEmployeeById(ownerId!, id!, {
		query: {
			enabled: !!ownerId && !!id && isEmployeeInPath,
		},
	});
	const { data: customerData, isLoading: isCustomerLoading } = useGetCustomerById(ownerId!, id!, {
		query: {
			enabled: !!ownerId && !!id && isCustomerInPath,
		},
	});

	const [breadcrumbPaths, setBreadcrumbPaths] = React.useState<BreadcrumbPath[]>([]);

	React.useEffect(() => {
		const splitPaths = pathname.split('/').filter((path) => path !== '');

		if (isTaskInPath) {
			console.log('taskData', taskData);
			console.log('jobData', jobData);
			if ((!taskData && !isNewInPath) || !jobData) return;

			setBreadcrumbPaths([
				{ slug: t('jobs'), path: `/app/owners/${ownerId}/jobs`, isLast: false },
				{
					slug: jobData!.title || t('job'),
					path: `/app/owners/${ownerId}/jobs/${jobData!.id}`,
					isLast: false,
				},
				...(isNewInPath
					? [
							{ slug: t('tasks'), path: `/app/owners/${ownerId}/jobs/${jobData!.id}/`, isLast: false },
							{ slug: g('add'), path: pathname, isLast: true },
					  ]
					: [
							{ slug: t('tasks'), path: `/app/owners/${ownerId}/jobs/${jobData!.id}`, isLast: false },
							{
								slug: taskData?.name || t('task'),
								path: `/app/owners/${ownerId}/tasks/${taskData?.id}`,
								isLast: !isEditInPath,
							},
							...(isEditInPath ? [{ slug: t('edit'), path: pathname, isLast: true }] : []),
					  ]),
			]);
			return;
		}

		const generateBreadcrumbPaths = () => {
			const pathsArray = splitPaths.map((path, index, arr) => {
				const isLast = index === splitPaths.length - 1;

				// Root dashboard (e.g., /app/owners/[ownerId])
				if (index === 2 && isLast) {
					return { slug: t('dashboard'), path: pathname, isLast };
				}

				// Section level (e.g., employees, jobs, customers)
				if (index === 3 && path !== 'tasks') {
					return { slug: t(path), path: `/app/owners/${ownerId}/${path}`, isLast };
				}

				// "new" path (e.g., /app/owners/[ownerId]/[section]/new)
				if (index === 4 && path === 'new') {
					return { slug: g('add'), path: `/app/owners/${ownerId}/${arr[3]}/new`, isLast };
				}

				// ID level (e.g., /app/owners/[ownerId]/[section]/[id])
				if (index === 4 && id === path) {
					if (isJobsInPath && jobData) {
						return {
							slug: jobData.title || t('job'),
							path: `/app/owners/${ownerId}/${arr[3]}/${arr[4]}`,
							isLast,
						};
					}
					if (isEmployeeInPath && employeeData) {
						return {
							slug: `${employeeData.firstName} ${employeeData.surName}` || t('employee'),
							path: `/app/owners/${ownerId}/${arr[3]}/${arr[4]}`,
							isLast,
						};
					}
					if (isCustomerInPath && customerData) {
						return {
							slug:
								customerData.companyName ||
								`${customerData.firstName} ${customerData.surName}` ||
								t('customer'),
							path: `/app/owners/${ownerId}/${arr[3]}/${arr[4]}`,
							isLast,
						};
					}
					// Fallback if data isn’t loaded yet
					return { slug: t(`breadcrumb.${arr[3]}`), path: pathname, isLast };
				}

				// Deeper paths (e.g., /app/owners/[ownerId]/[section]/[id]/[subpath])
				if (index === 5) {
					return {
						slug: t(`breadcrumb.${path}`),
						path: `/app/owners/${ownerId}/${arr[3]}/${arr[4]}/${path}`,
						isLast,
					};
				}

				return null;
			});

			const filteredPaths = pathsArray.filter((item): item is BreadcrumbPath => item !== null);
			setBreadcrumbPaths(filteredPaths);
		};

		generateBreadcrumbPaths();
	}, [
		pathname,
		ownerId,
		id,
		t,
		g,
		jobData,
		employeeData,
		customerData,
		taskData,
		isJobsInPath,
		isTaskInPath,
		isEmployeeInPath,
		isCustomerInPath,
		isEditInPath,
		isNewInPath,
	]);

	// Handle loading state or missing ownerId
	if (!ownerId || isJobLoading || isEmployeeLoading || isCustomerLoading || isTaskLoading) {
		return <BreadcrumbRoot>Loading...</BreadcrumbRoot>;
	}

	return (
		<BreadcrumbRoot>
			{breadcrumbPaths.map((path) => (
				<React.Fragment key={path.path}>
					{path.isLast ? (
						<BreadcrumbCurrentLink fontWeight="semibold">{path.slug}</BreadcrumbCurrentLink>
					) : (
						<BreadcrumbLink
							href={path.path}
							onClick={(e) => {
								e.preventDefault();
								if (onClose) onClose();
								router.push(path.path);
							}}
						>
							{path.slug}
						</BreadcrumbLink>
					)}
				</React.Fragment>
			))}
		</BreadcrumbRoot>
	);
}
